// Mirrored in applications/platform-app/components/web-analytics — change both.
import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, Modal, Note } from './ui';
import { RUBY, RUBY_TINT, bucketLabel } from './format';

/** Custom calendar (never a native date picker). Pick a start, then an end; max 366 days, nothing in the future. */
const MAX_DAYS = 366;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const iso = (y: number, m: number, d: number) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000) + 1;

export const CustomRangeModal: React.FC<{
  initial?: { from?: string; to?: string };
  today?: string;
  onApply: (from: string, to: string) => void;
  onClose: () => void;
}> = ({ initial, today: todayProp, onApply, onClose }) => {
  const now = new Date();
  const today = todayProp || iso(now.getFullYear(), now.getMonth(), now.getDate());
  const start = initial?.from || today;
  const [view, setView] = useState({ y: +start.slice(0, 4), m: +start.slice(5, 7) - 1 });
  const [a, setA] = useState<string | null>(initial?.from || null);
  const [b, setB] = useState<string | null>(initial?.to || null);

  const cells = useMemo(() => {
    const first = new Date(Date.UTC(view.y, view.m, 1)).getUTCDay();
    const days = new Date(Date.UTC(view.y, view.m + 1, 0)).getUTCDate();
    return { first, days };
  }, [view]);

  const pick = (d: string) => {
    if (!a || b) { setA(d); setB(null); return; }
    if (d < a) { setA(d); return; }
    setB(d);
  };
  const tooLong = !!(a && b && dayDiff(a, b) > MAX_DAYS);
  const move = (k: number) => setView((v) => { const m = v.m + k; return { y: v.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 }; });

  return (
    <Modal
      title="Custom range" onClose={onClose} width={420} testId="wa-custom-modal"
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button tone="ruby" disabled={!a || !b || tooLong} onClick={() => a && b && onApply(a, b)} data-testid="wa-custom-apply">Apply</Button>
      </>}
    >
      <div className="flex items-center gap-2">
        <button type="button" aria-label="Previous month" onClick={() => move(-1)} className="w-11 h-11 rounded-lg bg-stone-50 border-0 inline-flex items-center justify-center"><ChevronLeft size={16} /></button>
        <b className="flex-1 text-center">{MONTHS[view.m]} {view.y}</b>
        <button type="button" aria-label="Next month" onClick={() => move(1)} disabled={iso(view.y, view.m, 1) > today} className="w-11 h-11 rounded-lg bg-stone-50 border-0 inline-flex items-center justify-center disabled:opacity-40"><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 mt-2">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} className="text-[10px] text-stone-500 text-center p-1">{d}</div>)}
        {Array.from({ length: cells.first }, (_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: cells.days }, (_, k) => {
          const d = iso(view.y, view.m, k + 1);
          const edge = d === a || d === b;
          const inside = !!(a && b && d > a && d < b);
          const future = d > today;
          return (
            <button
              key={d} type="button" disabled={future} onClick={() => pick(d)} data-day={d}
              className="h-11 border-0 rounded-lg text-xs disabled:opacity-30"
              style={edge ? { background: RUBY, color: '#fff' } : inside ? { background: RUBY_TINT, color: '#9F1239' } : { background: '#fafaf9' }}
            >
              {k + 1}
            </button>
          );
        })}
      </div>
      <Note>
        {tooLong ? `A custom range can be at most ${MAX_DAYS} days.`
          : a ? (b ? `${bucketLabel(a)} – ${bucketLabel(b)} (${dayDiff(a, b)} days)` : `From ${bucketLabel(a)}. Now pick an end date.`) : 'Pick a start date.'}
      </Note>
    </Modal>
  );
};
